/**
 * @typedef {import('@/types/structured-data-editor').SdeRow} SdeRow
 */

const SYNC_RESULT_META_KEYS = new Set([
    "status",
    "action",
    "line",
    "data",
    "item",
    "instance",
    "errors",
]);

const METADATA_ERROR_PATTERNS = [
    /Métadonnée\s+'([^']+)'/i,
    /Metadata\s+'([^']+)'/i,
    /Métadonnée\s+"([^"]+)"/i,
    /Metadata\s+"([^"]+)"/i,
];

/**
 * @param {unknown} value
 * @returns {string[]}
 */
function asStringList(value) {
    if (value == null) {
        return [];
    }
    if (Array.isArray(value)) {
        return value.map(String).filter(Boolean);
    }
    return [String(value)];
}

/**
 * @param {string} label
 * @returns {string}
 */
function normalizeLabel(label) {
    return String(label).trim().toLowerCase();
}

/**
 * @param {Record<string, { label?: string }>} metadataFieldInfoMap
 * @returns {Record<string, string>}
 */
export function buildMetadataLabelToFieldKeyMap(metadataFieldInfoMap = {}) {
    /** @type {Record<string, string>} */
    const map = {};
    Object.entries(metadataFieldInfoMap).forEach(([code, info]) => {
        const label = normalizeLabel(info?.label || code);
        map[label] = `metadata.${code}`;
    });
    return map;
}

/**
 * @param {object|null|undefined} item
 * @returns {Record<string, unknown>|null}
 */
export function extractSyncErrors(item) {
    if (!item || typeof item !== "object") {
        return null;
    }
    if (item.errors && typeof item.errors === "object" && Object.keys(item.errors).length) {
        return item.errors;
    }
    if (typeof item.error === "string" && item.error.trim()) {
        return { non_field_errors: [item.error] };
    }
    if (item.status === "ko" && item.errors) {
        return item.errors;
    }

    const keys = Object.keys(item).filter((key) => !SYNC_RESULT_META_KEYS.has(key));
    if (!keys.length) {
        return null;
    }

    const looksLikeErrors = keys.some(
        (key) =>
            key === "non_field_errors" ||
            Array.isArray(item[key]) ||
            (key === "metadata" && typeof item[key] === "object")
    );
    if (!looksLikeErrors) {
        return null;
    }

    /** @type {Record<string, unknown>} */
    const errors = {};
    keys.forEach((key) => {
        errors[key] = item[key];
    });
    return errors;
}

/**
 * @param {object|null|undefined} item
 * @returns {boolean}
 */
export function isSyncErrorItem(item) {
    return extractSyncErrors(item) !== null;
}

/**
 * @param {object[]|null|undefined} syncPayload
 * @returns {boolean}
 */
export function syncPayloadHasErrors(syncPayload) {
    return (syncPayload || []).some((item) => isSyncErrorItem(item));
}

/**
 * @param {string} message
 * @param {Record<string, string>} labelToFieldKey
 * @returns {string|null}
 */
export function resolveFieldKeyFromErrorMessage(message, labelToFieldKey) {
    for (const pattern of METADATA_ERROR_PATTERNS) {
        const match = message.match(pattern);
        if (!match?.[1]) {
            continue;
        }
        const fieldKey = labelToFieldKey[normalizeLabel(match[1])];
        if (fieldKey) {
            return fieldKey;
        }
    }
    return null;
}

/**
 * @param {Record<string, unknown>} errors
 * @param {{ metadataFieldInfoMap?: Record<string, { label?: string }>, columns?: Array<{ key: string }> }} [options]
 * @returns {{ fieldErrors: Record<string, string>, rowErrors: string[] }}
 */
export function mapSyncErrorsToFieldKeys(errors, options = {}) {
    /** @type {Record<string, string>} */
    const fieldErrors = {};
    /** @type {string[]} */
    const rowErrors = [];
    const labelToFieldKey = buildMetadataLabelToFieldKeyMap(options.metadataFieldInfoMap);
    const standardKeys = new Set((options.columns || []).map((column) => column.key));

    const appendFieldError = (fieldKey, message) => {
        if (!fieldKey || !message) {
            return;
        }
        fieldErrors[fieldKey] = fieldErrors[fieldKey]
            ? `${fieldErrors[fieldKey]}; ${message}`
            : message;
    };

    Object.entries(errors || {}).forEach(([key, rawMessages]) => {
        if (key === "non_field_errors") {
            asStringList(rawMessages).forEach((message) => {
                const fieldKey = resolveFieldKeyFromErrorMessage(message, labelToFieldKey);
                if (fieldKey) {
                    appendFieldError(fieldKey, message);
                } else {
                    rowErrors.push(message);
                }
            });
            return;
        }

        if (key === "metadata" && rawMessages && typeof rawMessages === "object") {
            Object.entries(rawMessages).forEach(([metaCode, metaMessages]) => {
                appendFieldError(
                    `metadata.${metaCode}`,
                    asStringList(metaMessages).join("; ")
                );
            });
            return;
        }

        let fieldKey = key;
        if (standardKeys.has(key)) {
            fieldKey = key;
        } else if (!key.startsWith("metadata.")) {
            fieldKey = `metadata.${key}`;
        }
        appendFieldError(fieldKey, asStringList(rawMessages).join("; "));
    });

    return { fieldErrors, rowErrors };
}

/**
 * @param {SdeRow} row
 * @returns {SdeRow}
 */
export function clearSyncErrorsFromRow(row) {
    const fields = { ...row.fields };
    Object.keys(fields).forEach((key) => {
        if (fields[key]?.syncError) {
            fields[key] = { ...fields[key], syncError: null };
        }
    });
    return {
        ...row,
        fields,
        syncStatus: row.syncStatus === "error" ? "pending" : row.syncStatus,
        syncErrors: undefined,
        syncRowErrors: undefined,
    };
}

/**
 * @param {SdeRow} row
 * @param {Record<string, string>} fieldErrors
 * @param {string[]} rowErrors
 * @returns {SdeRow}
 */
function applyFieldSyncErrors(row, fieldErrors, rowErrors) {
    const fields = { ...row.fields };

    Object.keys(fields).forEach((key) => {
        fields[key] = {
            ...fields[key],
            syncError: fieldErrors[key] || null,
        };
    });

    Object.entries(fieldErrors).forEach(([key, message]) => {
        if (fields[key]) {
            return;
        }
        fields[key] = {
            key,
            value: null,
            confidence: { score: null, level: "csv" },
            syncError: message,
        };
    });

    return {
        ...row,
        fields,
        syncRowErrors: rowErrors.length ? rowErrors : undefined,
    };
}

/**
 * @param {SdeRow[]} rows
 * @param {object[]} syncPayload
 * @param {{
 *   metadataFieldInfoMap?: Record<string, { label?: string }>,
 *   columns?: Array<{ key: string }>,
 *   persistSuccess?: boolean,
 * }} [options]
 * @returns {SdeRow[]}
 */
export function applySyncResultsToRows(rows, syncPayload, options = {}) {
    if (!syncPayload?.length) {
        return rows;
    }

    const persistSuccess = options.persistSuccess !== false;
    let payloadIndex = 0;
    return rows.map((row) => {
        if (row.excluded) {
            return row;
        }

        const result = syncPayload[payloadIndex];
        payloadIndex += 1;

        if (result === undefined) {
            return row;
        }

        const errors = extractSyncErrors(result);
        if (!errors) {
            if (!persistSuccess) {
                const nextStatus = row.syncStatus === "synced" ? "synced" : "pending";
                return clearSyncErrorsFromRow({ ...row, syncStatus: nextStatus });
            }
            return clearSyncErrorsFromRow({ ...row, syncStatus: "synced" });
        }

        const { fieldErrors, rowErrors } = mapSyncErrorsToFieldKeys(errors, options);
        return applyFieldSyncErrors(
            { ...row, syncStatus: "error", syncErrors: errors },
            fieldErrors,
            rowErrors
        );
    });
}

/**
 * @param {SdeRow} row
 * @returns {string}
 */
export function rowDisplayLabel(row) {
    const name = row.fields?.name?.value;
    if (name != null && String(name).trim() !== "") {
        return String(name);
    }
    const upc = row.fields?.upc?.value;
    if (upc != null && String(upc).trim() !== "") {
        return String(upc);
    }
    return `#${row.rowIndex + 1}`;
}

/**
 * @param {SdeRow} row
 * @returns {string[]}
 */
export function collectRowSyncErrorMessages(row) {
    /** @type {string[]} */
    const messages = [];
    const seen = new Set();

    const append = (message) => {
        const normalized = String(message || "").trim();
        if (!normalized || seen.has(normalized)) {
            return;
        }
        seen.add(normalized);
        messages.push(normalized);
    };

    Object.values(row.fields || {}).forEach((field) => {
        if (field?.syncError) {
            append(field.syncError);
        }
    });
    (row.syncRowErrors || []).forEach(append);
    asStringList(row.syncErrors?.non_field_errors).forEach(append);

    return messages;
}

/**
 * @param {SdeRow[]} rows
 * @returns {Array<{ label: string, messages: string[] }>}
 */
export function buildSyncErrorDialogItems(rows) {
    return rows
        .filter((row) => !row.excluded && row.syncStatus === "error")
        .map((row) => ({
            label: rowDisplayLabel(row),
            messages: collectRowSyncErrorMessages(row),
        }))
        .filter((item) => item.messages.length);
}

/**
 * @param {object[]|null|undefined} syncPayload
 * @returns {{ created: number, updated: number, errors: number }}
 */
export function summarizeSyncPayload(syncPayload) {
    const items = syncPayload || [];
    const created = items.filter(
        (item) => item?.status !== "ko" && item?.action === "create"
    ).length;
    const updated = items.filter(
        (item) => item?.status !== "ko" && item?.action === "update"
    ).length;
    const errors = items.filter((item) => isSyncErrorItem(item)).length;
    return { created, updated, errors };
}
